import { useEffect, useRef, useState } from 'react'
import { apiClient, ApiRequestError } from '@astro/api'
import { forgotPasswordSchema, resetOtpVerifySchema, resetPasswordSchema } from '@astro/validators'
import { useToast } from '../../context/ToastContext'
import { InputField, PasswordField, MailIcon, WhatsAppIcon } from './AuthControls'
import { toFieldErrors, userFacingMessage } from './formErrors'

const RESEND_SECONDS = 30
const OTP_LENGTH = 4
const LINK_POLL_MS = 4000

/** Step → the small uppercase subtitle under the panel title. */
const SUBTITLES = {
  email: 'Recover Your Access Key',
  choose: 'Choose Your Recovery Path',
  otp: 'Enter the Code We Sent',
  password: 'Choose Your New Access Key',
  'link-sent': 'Awaiting Your Secure Link',
  done: 'Access Key Rewritten',
}

const GOLD_BTN =
  'shine-btn w-full bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-sm tracking-[0.22em] uppercase py-4 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0'

const LABEL = 'block text-[11px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-2'

/**
 * Password reset, all in one panel:
 *
 *   email      — collect + verify the account email
 *   choose     — pick the recovery path: secure email link, or one-time code
 *   link-sent  — hold screen; polls until the password is set via the emailed
 *                link (which opens the separate /reset-password page)
 *   otp        — enter the 4-digit code
 *   password   — new password + confirmation (OTP path only)
 *   done       — success, hand back to sign-in
 */
export default function ForgotPasswordForm({ prefillEmail = '', onBackToLogin }) {
  const { showToast } = useToast()

  const [step, setStep] = useState('email')
  const [email, setEmail] = useState(prefillEmail)
  const [maskedMobile, setMaskedMobile] = useState('')
  // Link branch: the poll-able id returned by /forgot-password/link.
  const [requestId, setRequestId] = useState('')
  const [code, setCode] = useState(Array(OTP_LENGTH).fill(''))
  const [resetToken, setResetToken] = useState('')
  const [form, setForm] = useState({ password: '', password_confirmation: '' })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  // Which choose-card is in flight — both stay disabled, only one shows a spinner label.
  const [sending, setSending] = useState(null)
  const [resendCooldown, setResendCooldown] = useState(0)
  const inputsRef = useRef([])

  // Resend cooldown ticker — re-arms itself each second while > 0.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  // Autofocus the first OTP box as soon as the code-entry step appears.
  useEffect(() => {
    if (step === 'otp') inputsRef.current[0]?.focus()
  }, [step])

  // Hold screen: the emailed link opens /reset-password in its own tab, so this
  // panel only learns the password changed by asking. Poll until it has.
  useEffect(() => {
    if (step !== 'link-sent' || !requestId) return undefined
    let cancelled = false
    const timer = setInterval(async () => {
      try {
        const res = await apiClient.get('/forgot-password/link/status', { params: { request_id: requestId } })
        if (!cancelled && res?.data?.completed) {
          setStep('done')
          showToast('Password updated via your secure link.')
        }
      } catch {
        // Transient poll failures just mean "not yet" — keep waiting quietly.
      }
    }, LINK_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [step, requestId, showToast])

  const failToast = (err, fallback) => {
    if (err instanceof ApiRequestError) {
      setErrors(toFieldErrors(err.error.details))
      showToast(userFacingMessage(err, fallback))
    } else {
      showToast('Something went wrong. Please try again.')
    }
  }

  /** Step 1 — confirm the email belongs to an account before offering paths. */
  const handleVerifyEmail = async (event) => {
    event.preventDefault()
    const parsed = forgotPasswordSchema.safeParse({ email })
    if (!parsed.success) return setErrors(toFieldErrors(parsed.error.issues))

    setErrors({})
    setSubmitting(true)
    try {
      const res = await apiClient.post('/forgot-password/verify', { email: parsed.data.email })
      setMaskedMobile(res?.data?.mobile_masked ?? '')
      setStep('choose')
    } catch (err) {
      failToast(err, 'We could not find an account for that email.')
    } finally {
      setSubmitting(false)
    }
  }

  /** Choose-path A — email a secure link, then hold here until it's used. */
  const sendLink = async ({ resend = false } = {}) => {
    if (submitting || sending) return
    setErrors({})
    setSending('link')
    setSubmitting(true)
    try {
      const res = await apiClient.post('/forgot-password/link', { email })
      setRequestId(res?.data?.request_id ?? '')
      setStep('link-sent')
      setResendCooldown(RESEND_SECONDS)
      showToast(res?.message || (resend ? 'Reset link sent again.' : 'Secure reset link sent to your email.'))
    } catch (err) {
      failToast(err, 'Could not send the reset link. Please try again.')
    } finally {
      setSending(null)
      setSubmitting(false)
    }
  }

  /** Choose-path B — send a one-time code, then collect it on the otp step. */
  const sendOtp = async ({ resend = false } = {}) => {
    if (submitting || sending) return
    setErrors({})
    setSending('otp')
    setSubmitting(true)
    try {
      const res = await apiClient.post('/forgot-password/otp', { email })
      if (res?.data?.mobile_masked) setMaskedMobile(res.data.mobile_masked)
      setCode(Array(OTP_LENGTH).fill(''))
      setStep('otp')
      setResendCooldown(RESEND_SECONDS)
      showToast(res?.message || (resend ? 'OTP sent again.' : 'OTP sent to your registered WhatsApp number.'))
      if (resend) inputsRef.current[0]?.focus()
    } catch (err) {
      failToast(err, 'Could not send the OTP. Please try again.')
    } finally {
      setSending(null)
      setSubmitting(false)
    }
  }

  /** OTP step — exchange a valid code for a short-lived reset token. */
  const handleVerifyOtp = async (event) => {
    event.preventDefault()
    const otp = code.join('')
    const parsed = resetOtpVerifySchema.safeParse({ email, otp })
    if (!parsed.success) return setErrors(toFieldErrors(parsed.error.issues))

    setErrors({})
    setSubmitting(true)
    try {
      const res = await apiClient.post('/forgot-password/otp/verify', parsed.data)
      setResetToken(res?.data?.reset_token ?? '')
      setStep('password')
    } catch (err) {
      failToast(err, 'Invalid OTP. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  /** Final step of the OTP path — set the new password with the reset token. */
  const handleSetPassword = async (event) => {
    event.preventDefault()
    const parsed = resetPasswordSchema.safeParse({ ...form, email, token: resetToken })
    if (!parsed.success) return setErrors(toFieldErrors(parsed.error.issues))

    setErrors({})
    setSubmitting(true)
    try {
      const res = await apiClient.post('/reset-password', parsed.data)
      setStep('done')
      showToast(res?.message || 'Password updated. Please sign in with your new password.')
    } catch (err) {
      failToast(err, 'Could not reset your password. Please start over.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDigitChange = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1)
    setCode((prev) => {
      const next = [...prev]
      next[index] = digit
      return next
    })
    if (digit && index < OTP_LENGTH - 1) inputsRef.current[index + 1]?.focus()
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
  }

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    event.preventDefault()
    setCode((prev) => {
      const next = [...prev]
      for (let i = 0; i < OTP_LENGTH; i += 1) next[i] = pasted[i] ?? next[i]
      return next
    })
    inputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus()
  }

  const backToChoose = () => {
    setStep('choose')
    setErrors({})
    setResendCooldown(0)
  }

  return (
    <div id="view-forgot-frame" className="auth-view-panel">
      <div className="space-y-2 mb-8 text-center sm:text-left">
        <h2 className="text-3xl font-royal tracking-wide text-astroDark uppercase font-medium">
          {step === 'done' ? 'Password Updated' : step === 'password' ? 'Set New Password' : 'Reset Password'}
        </h2>
        <p className="text-[11px] uppercase font-bold tracking-[0.25em] text-amber-800/60">{SUBTITLES[step]}</p>
        {step !== 'done' && (
          <p className="text-sm text-stone-500 pt-1">
            <button type="button" onClick={() => onBackToLogin(email)} className="text-goldAccent font-semibold hover:underline outline-none focus:underline">← Back to Password Login</button>
          </p>
        )}
      </div>

      {step === 'email' && (
        <form onSubmit={handleVerifyEmail} className="space-y-5" noValidate>
          <p className="text-sm text-stone-500 leading-relaxed">
            Enter the email address on your account. Once we verify it, you can choose how to reset your password.
          </p>

          <InputField
            label="Email Address"
            labelClass={LABEL}
            icon={<MailIcon />}
            type="email"
            id="forgot-email"
            autoComplete="email"
            required
            placeholder="seeker@path.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
          />

          <button type="submit" disabled={submitting} className={`${GOLD_BTN} mt-2`}>
            {submitting ? 'Verifying…' : 'Continue →'}
          </button>
        </form>
      )}

      {step === 'choose' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>Account found for <span className="font-semibold text-astroDark break-all">{email}</span></span>
            <button type="button" onClick={() => { setStep('email'); setErrors({}) }} className="text-goldAccent font-semibold hover:underline outline-none focus:underline shrink-0 ml-3">Change</button>
          </div>

          <p className="text-sm text-stone-500 leading-relaxed">How would you like to reset your password?</p>

          <button
            type="button"
            onClick={() => sendLink()}
            disabled={submitting}
            className="w-full text-left p-5 border border-amber-800/20 bg-amber-50/40 rounded-2xl flex items-start gap-4 transition-colors hover:border-goldAccent/60 hover:bg-amber-50/70 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            <div className="w-10 h-10 rounded-xl bg-white/70 border border-amber-800/15 flex items-center justify-center text-goldAccent shrink-0">
              <MailIcon />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-amber-900 leading-tight">
                {sending === 'link' ? 'Sending Secure Link…' : 'Secure Link via Email'}
              </p>
              <p className="text-sm text-stone-500 font-light leading-relaxed mt-1">
                We'll email you a one-time link that opens a secure page to set your new password.
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => sendOtp()}
            disabled={submitting}
            className="w-full text-left p-5 border border-amber-800/20 bg-amber-50/40 rounded-2xl flex items-start gap-4 transition-colors hover:border-goldAccent/60 hover:bg-amber-50/70 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            <div className="w-10 h-10 rounded-xl bg-white/70 border border-amber-800/15 flex items-center justify-center shrink-0">
              <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-amber-900 leading-tight">
                {sending === 'otp' ? 'Sending OTP…' : 'One-Time Code (OTP)'}
              </p>
              <p className="text-sm text-stone-500 font-light leading-relaxed mt-1">
                We'll send a {OTP_LENGTH}-digit code to your registered WhatsApp number
                {maskedMobile ? <> <span className="font-semibold text-astroDark">{maskedMobile}</span></> : ''} — verify it here and set a new password right away.
              </p>
            </div>
          </button>
        </div>
      )}

      {step === 'link-sent' && (
        <div className="space-y-5">
          <div className="p-5 border border-amber-800/20 bg-amber-50/40 rounded-2xl flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/70 border border-amber-800/15 flex items-center justify-center text-goldAccent shrink-0">
              <MailIcon />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-amber-900 leading-tight">Secure link on its way</p>
              <p className="text-sm text-stone-500 font-light leading-relaxed mt-1">
                We've emailed a password reset link to{' '}
                <span className="font-semibold text-astroDark break-all">{email}</span>. Open it to set your new
                password — it expires shortly, so use it soon.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm text-stone-500">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-goldAccent opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-goldAccent" />
            </span>
            Keep this page open — it updates by itself once your new password is set.
          </div>

          <button
            type="button"
            onClick={() => sendLink({ resend: true })}
            disabled={submitting || resendCooldown > 0}
            className={GOLD_BTN}
          >
            {submitting ? 'Sending…' : resendCooldown > 0 ? `Resend Link in ${resendCooldown}s` : 'Resend Reset Link'}
          </button>

          <p className="text-center text-sm text-stone-500">
            No email after a minute? Check spam, or{' '}
            <button type="button" onClick={backToChoose} className="text-goldAccent font-semibold hover:underline outline-none focus:underline">choose another method</button>
          </p>
        </div>
      )}

      {step === 'otp' && (
        <form onSubmit={handleVerifyOtp} className="space-y-5" noValidate>
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>
              Code sent to your WhatsApp
              {maskedMobile ? <> <span className="font-semibold text-astroDark">{maskedMobile}</span></> : ' number'}
            </span>
            <button type="button" onClick={backToChoose} className="text-goldAccent font-semibold hover:underline outline-none focus:underline shrink-0 ml-3">Change method</button>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-3">Enter OTP</label>
            <div className="flex justify-between gap-2">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputsRef.current[index] = el }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  className="otp-box"
                  aria-invalid={Boolean(errors.otp)}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                />
              ))}
            </div>
            {errors.otp && <p className="text-xs text-red-600 mt-1.5 tracking-wide">{errors.otp}</p>}
          </div>

          <button type="submit" disabled={submitting} className={`${GOLD_BTN} mt-2`}>
            {submitting ? 'Verifying…' : 'Verify Code →'}
          </button>

          <p className="text-center text-sm text-stone-500">
            {resendCooldown > 0 ? (
              <>Resend OTP in <span className="font-semibold text-astroDark">{resendCooldown}s</span></>
            ) : (
              <button type="button" onClick={() => sendOtp({ resend: true })} disabled={submitting} className="text-goldAccent font-semibold hover:underline outline-none focus:underline disabled:opacity-60 disabled:no-underline">Resend OTP</button>
            )}
          </p>
        </form>
      )}

      {step === 'password' && (
        <form onSubmit={handleSetPassword} className="space-y-4" noValidate>
          <p className="text-sm text-stone-500 leading-relaxed">
            Code verified. Choose a new password for{' '}
            <span className="font-semibold text-astroDark break-all">{email}</span>.
          </p>

          <PasswordField
            label="New Password"
            labelClass={LABEL}
            autoComplete="new-password"
            required
            placeholder="Minimum 8 complex characters"
            value={form.password}
            onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
            error={errors.password}
          />
          <PasswordField
            label="Confirm New Password"
            labelClass={LABEL}
            autoComplete="new-password"
            required
            placeholder="Re-enter your new password"
            value={form.password_confirmation}
            onChange={(e) => setForm((prev) => ({ ...prev, password_confirmation: e.target.value }))}
            error={errors.password_confirmation}
          />

          {/* The token came from OTP verification, so surface its rejection somewhere. */}
          {errors.token && <p className="text-xs text-red-600 tracking-wide">{errors.token}</p>}

          <button type="submit" disabled={submitting} className={`${GOLD_BTN} mt-2`}>
            {submitting ? 'Updating Password…' : 'Update Password →'}
          </button>
        </form>
      )}

      {step === 'done' && (
        <div className="space-y-5">
          <div className="p-5 border border-amber-800/20 bg-amber-50/40 rounded-2xl flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/70 border border-amber-800/15 flex items-center justify-center text-goldAccent text-lg shrink-0">✓</div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-amber-900 leading-tight">Your password has been changed</p>
              <p className="text-sm text-stone-500 font-light leading-relaxed mt-1">
                Sign in with your new password to continue.
              </p>
            </div>
          </div>

          <button type="button" onClick={() => onBackToLogin(email)} className={GOLD_BTN}>
            Sign In Now →
          </button>
        </div>
      )}
    </div>
  )
}
